import i18next from 'i18next';
import { isEmpty, trim } from 'lodash';
import * as Yup from 'yup';

import { FormFieldType } from 'enums';
import { buildFormValidator } from 'features/form-builder';
import { FormField } from 'types';
import { validationUtils } from 'utils';

/**
 * Build common validator for requisites form
 */
const buildCommonValidator = (): Yup.ObjectSchema<any> => {
  const tCommon = i18next.t;

  const limitFieldSchema = Yup.number()
    .integer(tCommon('errors.natural_number'))
    .moreThan(0, tCommon('errors.natural_number'))
    .required(tCommon('errors.required'));

  return Yup.object().shape({
    fiatCurrencyId: Yup.string().required(tCommon('errors.required')),
    paymentTypeId: Yup.string().when('fiatCurrencyId', {
      is: (fiatCurrencyId: string) => !!fiatCurrencyId,
      then: (schema) => schema.required(tCommon('errors.required')),
      otherwise: (schema) => schema.nullable(),
    }),
    bankId: Yup.string().when('paymentTypeId', {
      is: (paymentTypeId: string) => !!paymentTypeId,
      then: (schema) => schema.required(tCommon('errors.required')),
      otherwise: (schema) => schema.nullable(),
    }),
    sameBank: Yup.boolean().optional().nullable(),
    isWhiteTriangle: Yup.boolean().optional().nullable(),
    allowTurboDeposit: Yup.boolean().optional().nullable(),
    status: Yup.string().required(tCommon('errors.required')),
    ...validationUtils.getNaturalNumberRangeValidationSchema(
      'minOrderAmount',
      'maxOrderAmount',
    ),
    limitActiveOrders: limitFieldSchema,
    limitCountPerHour: limitFieldSchema,
    limitCountPerDay: limitFieldSchema,
    limitCountPerMonth: limitFieldSchema,
    limitSumPerHour: limitFieldSchema,
    limitSumPerDay: limitFieldSchema,
    limitSumPerMonth: limitFieldSchema,
    completedOrdersInMinutes: Yup.number()
      .integer(tCommon('errors.natural_or_zero_number'))
      .min(0, tCommon('errors.natural_or_zero_number'))
      .required(tCommon('errors.required')),
    name: Yup.string().optional().nullable(),
    groupId: Yup.string().required(tCommon('errors.required')),
    accountLastDigits: Yup.string().optional().nullable(),
    counterpartiesOnly: Yup.boolean().optional().nullable(),
    qrManagerEnabled: Yup.boolean().optional().nullable(),
  });
};

/**
 * Build custom validator for requisites form
 *
 * @param fields - requisites form fields
 */
export const buildValidator = (fields?: FormField[] | null) => {
  const tCommon = i18next.t;
  const commonValidator = buildCommonValidator();
  if (!fields || !fields.length) {
    return commonValidator;
  }
  const validator = commonValidator.concat(buildFormValidator(fields));

  const paymentLinkField = fields.find(
    (field) => field.type === FormFieldType.PaymentLink,
  );
  const hasPaymentString = fields.some(
    (field) => field.type === FormFieldType.PaymentString,
  );

  // Payment link is optional when a payment string is provided (SberQR Vietnam)
  if (paymentLinkField && hasPaymentString) {
    return validator.shape({
      paymentLink: Yup.string()
        .url(tCommon('errors.invalid'))
        .matches(/^https:\/\//, tCommon('errors.invalid'))
        .when('paymentString', {
          is: (paymentString: string) => !isEmpty(trim(paymentString)),
          then: (schema) => schema.nullable(),
          otherwise: (schema) =>
            paymentLinkField.required
              ? schema.required(tCommon('errors.required'))
              : schema.nullable(),
        }),
    });
  }

  return validator;
};
